/**
 * One row of an entity tree, shared by ServiceBrowser and SiteConfigBrowser.
 *
 * Both browsers previously declared a near-identical `Row` INSIDE their own
 * component body. That is the expensive kind of duplication: a component
 * defined during render gets a fresh identity every time its parent renders,
 * so React unmounts and remounts the whole subtree instead of updating it.
 * Since both browsers hold their search box in local state, every keystroke
 * remounted the entire tree.
 *
 * Declaring it once at module scope fixes that for both. The row is generic
 * over the entity-kind string so each browser keeps its own union type.
 */

import { useContext } from "react"
import { PendingDot, PendingIdsContext } from "./PendingChanges"
import { memo } from "react"
import { cn } from "@/lib/utils"
import { ChevronDown, ChevronRight, ChevronUp, Plus, Trash2 } from "lucide-react"
import type { TranslationStatus } from "../languages"
import { StatusDot } from "./languages/StatusDot"

export interface TreeRowProps<K extends string = string> {
  kind: K
  id: string
  /** A node, so a caller can style part of it (e.g. a dimmed type prefix). */
  label: React.ReactNode
  /** Plain-text label for the hover tooltip, when `label` is a styled node. */
  labelText?: string
  icon: React.ReactNode
  /** Indent level; each step is 12px. */
  depth: number
  selected: boolean
  onSelect: (kind: K, id: string) => void
  onDelete: (kind: K, id: string) => void
  badge?: React.ReactNode
  expandable?: boolean
  isOpen?: boolean
  onToggle?: () => void
  /** Renders a "+" action on hover when supplied. */
  onAdd?: () => void
  addTitle?: string
  // The record's state in the working language; only missing and needs-review show a dot
  status?: TranslationStatus
  statusTitle?: string
  /** Set false for a row that is a view of its parent rather than its own record. */
  deletable?: boolean
  /**
   * Reorder controls, for lists whose order is meaningful (e.g. page-content
   * blocks, where array order is render order). Omit an end of the range to
   * disable that direction at the list boundary.
   */
  onMoveUp?: () => void
  onMoveDown?: () => void
}

function TreeRowImpl<K extends string>({
  kind, id, label, labelText, icon, depth, selected, onSelect, onDelete,
  badge, expandable, isOpen, onToggle, onAdd, addTitle, status, statusTitle,
  deletable = true, onMoveUp, onMoveDown,
}: TreeRowProps<K>) {
  const pendingIds = useContext(PendingIdsContext)
  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded px-1 py-1 text-sm cursor-pointer",
        selected ? "bg-primary/10 text-foreground" : "hover:bg-muted/60",
      )}
      style={{ paddingLeft: depth * 12 + 4 }}
      onClick={() => onSelect(kind, id)}
    >
      {expandable ? (
        <button
          type="button"
          className="shrink-0 text-muted-foreground hover:text-foreground"
          aria-label={isOpen ? "Collapse" : "Expand"}
          aria-expanded={isOpen}
          onClick={e => { e.stopPropagation(); onToggle?.() }}
        >
          {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      ) : (
        <span className="w-3.5 shrink-0" />
      )}
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      {/*
        The label truncates but does NOT stretch, so its badge sits beside it
        rather than being flung against the panel edge with a gap of nothing
        between them. `min-w-0` is what actually lets truncate work inside a
        flex row; the spacer after the badge takes up the slack, keeping the
        row-hover highlight and the right-aligned actions where they were.
      */}
      {/* title so a truncated label is still readable on hover. */}
      <span className="min-w-0 truncate" title={labelText ?? (typeof label === "string" ? label : undefined)}>
        {label}
      </span>
      {(status === "missing" || status === "unreviewed") && <StatusDot status={status} title={statusTitle} />}
      {pendingIds.has(id) && <PendingDot />}
      {badge}
      <span className="flex-1" />
      {onAdd && (
        <button
          type="button"
          className="shrink-0 text-muted-foreground/60 hover:text-foreground"
          title={addTitle}
          aria-label={addTitle}
          onClick={e => { e.stopPropagation(); onAdd() }}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      )}
      {(onMoveUp || onMoveDown) && (
        <span className="flex shrink-0 items-center opacity-0 group-hover:opacity-100">
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
            title="Move up"
            aria-label="Move up"
            disabled={!onMoveUp}
            onClick={e => { e.stopPropagation(); onMoveUp?.() }}
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
            title="Move down"
            aria-label="Move down"
            disabled={!onMoveDown}
            onClick={e => { e.stopPropagation(); onMoveDown?.() }}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </span>
      )}
      {deletable && (
        <button
          type="button"
          className="shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
          title={`Delete ${kind}`}
          aria-label={`Delete ${kind}`}
          onClick={e => { e.stopPropagation(); onDelete(kind, id) }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

/**
 * memo() keeps rows that did not change from re-rendering when a sibling does
 * — worthwhile here because a tree renders hundreds of these and the parent
 * re-renders on every search keystroke. The cast preserves the generic
 * signature, which memo's own types erase.
 */
export const TreeRow = memo(TreeRowImpl) as typeof TreeRowImpl
